import { bindable, bindingMode, computedFrom, autoinject } from 'aurelia-framework';
import numeral from 'numeral';
import * as core from 'core-js';
import { State } from '../../../services/global-state';
import { PayrazrService } from '../../../services/payrazr';
import { I18N } from 'aurelia-i18n';
import { Account } from '../../../models/customer';
import { SelectedAccountsSettings } from './selected-accounts-settings';

const INSTALLMENT_STATES = [200, 201, 299];
const SINGLE_PAYMENT_STATES = [300, 301];
const RECURRING_STATES = [400, 401, 499];
const ACTIVE_STATUS = 1;
const INACTIVE_STATUS = 2;

@autoinject
export class AccountPaymentCards {
	@bindable selectedAccounts: Array<Account>;
	cachedSelectedAccountsSettings: SelectedAccountsSettings;
	cards: Array<any> = [];
	hasPlan: boolean = undefined;
	@bindable({ defaultBindingMode: bindingMode.twoWay }) hasFocus;
	error: string;

	constructor(private state: State, private payrazrService: PayrazrService, private i18n: I18N) {

	}

	attached() {
		if (this.selectedAccounts && this.selectedAccounts.length) {
			this.getAvailableCards();
		}
		this.hasFocus = true;
	}

	selectedAccountsChanged(newAccounts: Array<Account>, oldAccounts: Array<Account>) {
		// Reset cached values so it loops through and recalculates
		this.hasPlan = undefined;
		this.cachedSelectedAccountsSettings = undefined;

		this.getAvailableCards();
	}

	getAvailableCards() {
		this.cards = [];
		this.error = null;

		let isMinMaxValid = (this.selectedAccountsSettings.minPayment != null && this.selectedAccountsSettings.maxPayment != null)
			? (this.selectedAccountsSettings.maxPayment > this.selectedAccountsSettings.minPayment)
			: true;

		let isPaymentMethodsValid = (!this.selectedAccountsSettings.allowAch && !this.selectedAccountsSettings.allowCC && !this.selectedAccountsSettings.allowApplePay) ? false : true;

		if (this.selectedAccounts.length > 0 && (!isMinMaxValid || !isPaymentMethodsValid)) {
			this.error = this.i18n.i18next.t('selectedAccountsIncompatibility', 'Selected accounts have incompatible payment requirements.');
		} else {
			if (this.showCardPayInFull()) {
				this.cards.push({
					view: './cards/card-pay-in-full',
				});
			}

			if (this.showCardSingle()) {
				this.cards.push({
					view: './cards/card-single-payment',
				});
			}

			if (this.showCardInstallmentSetup()) {
				this.cards.push({
					view: './cards/card-installment-setup',
				});
			} else if (this.showCardInstallmentEdit()) {
				this.cards.push({
					view: './cards/card-installments-edit',
				});
			}

			if (this.showCardNegotiator()) {
				this.cards.push({
					view: './cards/card-negotiator',
				});
			}

			if (this.showCardRecurringSetup()) {
				this.cards.push({
					view: './cards/card-recurring-setup',
				});
			} else if (this.showCardRecurringEdit()) {
				this.cards.push({
					view: './cards/card-recurring-edit',
				});
			}

			if (this.showCardAutopaySetup()) {
				this.cards.push({
					view: './cards/card-autopay-setup',
				});
			} else if (this.showCardAutopayEdit()) {
				this.cards.push({
					view: './cards/card-autopay-edit',
				});
			}
		}
	}

	showCardPayInFull(): boolean {
		let show = false;

		if (!this.hasActiveInstallment()) {
			// Show if configured for installments within a range configured for pay in full
            this.selectedAccounts.forEach((account) => {
                if(account.balance > 0){
                    const ranges: Array<any> = this.selectedAccountsSettings.installmentBalanceRanges[account.id];
                    const range = ranges.find(r => account.balance >= r.min && account.balance <= r.max);
                    if(range && range.payInFull){
                        show = true;
                    }
                }
            });
		}
		return show;
	}

	showCardSingle(): boolean {
		let show = false;

		if (!this.hasActiveInstallment()) {
			// Show if configured for installments with a range configured for pay in full
			if (this.selectedAccountsSettings.allowSingle || this.selectedAccountsSettings.allowFuture) {
				show = true;
			}
		}

		return show;
	}

	showCardInstallmentSetup(): boolean {
		let show = false;
		if (
			(this.selectedAccounts.length === 1
				|| (this.isMappAllowed()
					&& this.selectedAccounts.length > 1))
			&& this.hasPositiveBalance()
			&& this.selectedAccountsSettings.allowInstallments
			&& !this.hasActivePlan()
		) {
			if (this.isMappAllowed()
				&& this.selectedAccounts.length > 1
				&& !(this.selectedAccounts.filter((item, pos, self) => self.findIndex(v => v.portfolioName === item.portfolioName) === pos).map(x => x.portfolioName).length === 1)) {
					return show;
			}

			let accountsBalance = this.selectedAccounts
				.map(x => x.balance)
				.reduce((sum, currentBalance) => sum + currentBalance, 0);

			for (let ixAccount = 0; ixAccount < this.selectedAccounts.length; ixAccount++) {
				const account = this.selectedAccounts[ixAccount];
				let balanceRanges = this.selectedAccountsSettings.installmentBalanceRanges[account.id];
				if (balanceRanges && balanceRanges.filter(br => br.enableInstallments).length > 0 ) {
					balanceRanges = balanceRanges.filter(br => br.enableInstallments);
					for (let ixRange = 0; ixRange < balanceRanges.length; ixRange++) {
						const range = balanceRanges[ixRange];
						if (accountsBalance >= range.min
							&& accountsBalance <= range.max) {
								show = true;
								break;
						}
					}
					if (show) {
						break;
					}
				}
			}
		}

		return show;
	}

	showCardNegotiator(): boolean {
		let show = false;

		if (
			this.selectedAccounts.length === 1
			&& this.selectedAccountsSettings.allowInstallments
			&& !this.hasActivePlan()
		) {
			let account = this.selectedAccounts[0];
			let balanceRanges = this.selectedAccountsSettings.installmentBalanceRanges[account.id];
			if (balanceRanges) {
				// Assume only 1 account
				balanceRanges.forEach((range) => {
					// Find range within account balance
					if (
						account.balance >= range.min
						&& account.balance <= range.max
						&& range.enableNegotiator
					) {
						show = true;
					}
				});
			}
		}
		return show;
	}

	showCardRecurringSetup(): boolean {
		let show = false;

		// Show if one selected account and configured for installments with no existing active plans
		if (
			this.selectedAccounts.length === 1
			&& this.selectedAccountsSettings.allowRecurring
			&& !this.hasActivePlan()
		) {
			show = true;
		}

		return show;
	}

	showCardAutopaySetup(): boolean {
		let show = false;

		// Show if one selected account and configured for installments with no existing active plans
		if (
			this.selectedAccounts.length === 1
			&& this.selectedAccountsSettings.enableAutopay
			&& !this.hasActivePlan()
		) {
			show = true;
		}

		return show;
	}

	showCardAutopayEdit(): boolean {
		let show = false;

		// Show if one selected account and configured for installments with no existing active plans
		if (
			this.selectedAccounts.length === 1
			&& this.selectedAccountsSettings.enableAutopay
		) {
			this.selectedAccounts.forEach(account => {
				if (account.hasActiveAutopay) {
					show = true;
				}
			});
		}

		return show;
	}

	showCardRecurringEdit(): boolean {
		let show = false;

		// Show if one account selected and it has an active recurring plan 
		if (this.selectedAccounts.length === 1) {
			this.selectedAccounts[0].paymentPlans.forEach((plan) => {
				if (plan.status === ACTIVE_STATUS && plan.isRecurring) {
					show = true;
				}
			});
		}

		return show;
	}

	showCardInstallmentEdit(): boolean {
		let show = false;

		// Show if one account selected and it has an active installmenst plan 
		if (this.selectedAccounts.length === 1) {
			this.selectedAccounts[0].paymentPlans.forEach((plan) => {
				if (plan.status === ACTIVE_STATUS && !plan.isRecurring && !plan.isSingleFuture) {
					show = true;
				}
			});
		}

		return show;
	}

	get selectedAccountsSettings(): SelectedAccountsSettings {
		if (this.cachedSelectedAccountsSettings === undefined) {
			this.cachedSelectedAccountsSettings = new SelectedAccountsSettings();

			// Disable settings if any of the selected accounts have them disabled
			this.selectedAccounts.forEach((account) => {
				let accountPortfolio = this.state.business.fetchPortfolioSettings(account.portfolioName);

				if (!accountPortfolio.autopaySettings.enableAutopay) {
					this.cachedSelectedAccountsSettings.enableAutopay = false;
				}

				if (!accountPortfolio.settings.paymentTypes.allowInstallments) {
					this.cachedSelectedAccountsSettings.allowInstallments = false;
				}
				if (!accountPortfolio.settings.paymentTypes.allowRecurring) {
					this.cachedSelectedAccountsSettings.allowRecurring = false;
				}
				if (!accountPortfolio.settings.paymentTypes.allowSingle) {
					this.cachedSelectedAccountsSettings.allowSingle = false;
				}
				if (!accountPortfolio.settings.paymentTypes.allowFuture) {
					this.cachedSelectedAccountsSettings.allowFuture = false;
				}

				if (accountPortfolio.settings.paymentTypes.installments) {
					this.cachedSelectedAccountsSettings.installmentBalanceRanges[account.id] = accountPortfolio.settings.paymentTypes.installments.balanceRanges;
				}

				if (!accountPortfolio.settings.paymentMethods.allowCreditCard) {
					this.cachedSelectedAccountsSettings.allowCC = false;
				}

				if (!accountPortfolio.settings.paymentMethods.allowAch) {
					this.cachedSelectedAccountsSettings.allowAch = false;
				}

				if (!accountPortfolio.settings.paymentMethods.allowApplePay) {
					this.cachedSelectedAccountsSettings.allowApplePay = false;
				}

				let min = numeral(accountPortfolio.settings.minimumPaymentAmount).value();
				let max = numeral(accountPortfolio.settings.maximumPaymentAmount).value();

				// If min is defined, and if setting is undefined or if this min value is greater than the previously saved one
				if (
					(accountPortfolio.settings.minimumPaymentAmount !== undefined && accountPortfolio.settings.minimumPaymentAmount !== null)
					&& (this.cachedSelectedAccountsSettings.minPayment === undefined || this.cachedSelectedAccountsSettings.minPayment < min)
				){
					this.cachedSelectedAccountsSettings.minPayment = min;
				}

				// If max is defined, and if setting is undefined or if this max value is less than the previously saved one
				if (
					(accountPortfolio.settings.maximumPaymentAmount !== undefined && accountPortfolio.settings.maximumPaymentAmount !== null)
					&& (this.cachedSelectedAccountsSettings.maxPayment === undefined || this.cachedSelectedAccountsSettings.maxPayment > max)
				) {
					this.cachedSelectedAccountsSettings.maxPayment = max;
				}
			});
		}

		return this.cachedSelectedAccountsSettings;
	}

	hasActivePlan(): boolean {
		if (this.hasPlan === undefined) {
			this.hasPlan = false;

			this.selectedAccounts.forEach((account) => {
				account.paymentPlans.forEach((plan) => {
					if (!plan.isSingleFuture && plan.status === ACTIVE_STATUS) {
						this.hasPlan = true;
					}
				});

				if (account.hasActiveAutopay) {
					this.hasPlan = true;
				}
			});
		}

		return this.hasPlan;
	}

	hasPositiveBalance(): boolean {
		// Loops through all selected accounts and makes sure they all have positive balances
		let hasPositive = true;
		this.selectedAccounts.forEach(selected => {
			if (selected.balance <= 0) {
				hasPositive = false;
			}
		});
		return hasPositive;
	}

	hasActiveInstallment(): boolean {
		// Returns false if not setup for installments, or if no active installment exists
		return this.selectedAccountsSettings.allowInstallments
				? this.showCardInstallmentEdit()
				: false;
	}

	isMappAllowed(): boolean
	{
		return this.state.features.portalMultiAccountPaymentPlan
			&& this.state.business.portalSettings.businessSettings.enableMultiAccountPaymentPlans;
	}
}
